import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import { ReactNode } from "react";

import { searchInputEndAdornment } from "../utils/components";
import { cn, getInputDefaultStyles, isTrimmedEmpty } from "../utils/styling";
import { LabelLessTextField } from "./Input";
import { AdornmentLoading } from "./Loading";

export type AutocompleteFieldProps<T> = Omit<
  AutocompleteProps<T, boolean, boolean, boolean>,
  "classes" | "renderInput" | "sx"
> & {
  endAdornment?: ReactNode;
  height?: string;
  inputClassName?: string;
  inputRootClassName?: string;
  label?: string;
  listboxClassName?: string;
  paperClassName?: string;
  placeholder?: string;
  popperClassName?: string;
  popperDisablePortal?: boolean;
  startAdornment?: ReactNode;
};

const defaultPaperClassName = "overflow-hidden rounded-md bg-lds-surface-bright";

const defaultInputRootEndClassName = "!pr-4 font-ds-body-regular-lg";

const autocompletePopperSx = {
  "&& .MuiAutocomplete-option": {
    "&:hover": {
      backgroundColor: "var(--color-selection-subtle)",
    },
    backgroundColor: "var(--color-surface-bright)",
  },
  "&& .MuiAutocomplete-option.Mui-focused:not(.Mui-selected)": {
    backgroundColor: "var(--color-selection-subtle)",
  },
  "&& .MuiAutocomplete-option.Mui-selected:hover": {
    backgroundColor: "var(--color-selection-subtle)",
  },
  "&& .MuiAutocomplete-option.Mui-selected:not(:hover)": {
    backgroundColor: "var(--color-selection-strong)",
  },
  "& .MuiPaper-root": {
    backgroundColor: "var(--color-surface-bright)",
    padding: 0,
  },
} as const;

export function AutocompleteField<T>({
  className,
  componentsProps,
  disabled,
  height,
  inputClassName,
  inputRootClassName,
  inputValue,
  label,
  listboxClassName,
  ListboxProps,
  loading,
  paperClassName,
  placeholder,
  popperClassName,
  popperDisablePortal,
  startAdornment,
  ...rest
}: AutocompleteFieldProps<T>) {
  const mergedComponentsProps = {
    ...componentsProps,
    paper: {
      ...componentsProps?.paper,
      className: cn(defaultPaperClassName, componentsProps?.paper?.className, paperClassName),
    },
    popper: {
      ...componentsProps?.popper,
      className: cn("z-autocomplete_popper", componentsProps?.popper?.className, popperClassName),
      disablePortal: popperDisablePortal ?? componentsProps?.popper?.disablePortal,
      placement: componentsProps?.popper?.placement ?? "bottom-start",
      sx: {
        ...autocompletePopperSx,
        ...componentsProps?.popper?.sx,
      },
    },
  };

  const { textColor } = getInputDefaultStyles({
    disabled,
    HasError: false,
    isEmpty: isTrimmedEmpty(inputValue),
  });

  const endAdornment = loading ? <AdornmentLoading /> : searchInputEndAdornment.endAdornment;

  const placeholderText = label ?? placeholder;
  return (
    <Autocomplete<T, boolean, boolean, boolean>
      {...rest}
      blurOnSelect
      className={cn("w-full", className)}
      clearOnBlur={false}
      componentsProps={mergedComponentsProps}
      disabled={disabled}
      filterOptions={(x) => x}
      ListboxProps={{
        ...ListboxProps,
        className: cn(
          "max-h-[min(40vh,28rem)] overflow-y-auto font-ds-body-regular-lg",
          ListboxProps?.className,
          listboxClassName
        ),
      }}
      renderInput={(params) => (
        <LabelLessTextField
          {...params}
          disabled={disabled}
          displayshrinkedlabel="true"
          fullWidth
          height={height}
          hiddenLabel
          InputProps={{
            ...params?.InputProps,
            className: cn(params?.InputProps?.className, defaultInputRootEndClassName, inputRootClassName),
            endAdornment,
          }}
          placeholder={placeholderText}
          textcolor={textColor}
          variant="filled"
        />
      )}
      selectOnFocus
      size="small"
    />
  );
}
